import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiEye, FiEdit3, FiTrash2 } from 'react-icons/fi';
import { selectionProcessesAdmin } from '@/services/selectionProcesses';
import { PATHS } from '@/routes/paths';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableNoData, TableRow } from '@/components/ui/table';
import { Filter, FilterContent, FilterActions, FilterRow } from '@/components/custom/filter';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { confirm } from '@/lib/confirm';

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'open', label: 'Abertos' },
  { value: 'closed', label: 'Encerrados' }
];

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', variant: 'warning' },
  open: { label: 'Aberto', variant: 'success' },
  closed: { label: 'Encerrado', variant: 'neutral-lightest' }
};

const FALLBACK_STATUS = { label: '—', variant: 'neutral-lightest' };

const AUDIENCE_LABEL = { student: 'Aluno', teacher: 'Professor' };

const TABLE_HEAD = [{ label: 'Nome' }, { label: 'Status', width: 130 }, { label: 'Público', width: 110 }, { label: 'Inscritos', width: 100 }, { label: 'Encerra em', width: 170 }, { label: 'Ações', width: 320 }];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

export default function SelectionProcessesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const breadcrumbs = {
    title: 'Inscrições',
    nav: [
      {
        name: 'Seletivos',
        isActive: true
      }
    ]
  };

  const loadItems = () => {
    setLoading(true);

    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (debouncedSearch) params.q = debouncedSearch;

    return selectionProcessesAdmin
      .list(params)
      .then(res => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadItems();
  }, [statusFilter, debouncedSearch]);

  const filteredItems = useMemo(() => items, [items]);

  const handleDelete = async item => {
    const a = item.attributes;
    const confirmed = await confirm({
      title: 'Excluir seletivo?',
      description: `O seletivo "${a.name}" será removido permanentemente. Essa ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'destructive'
    });
    if (!confirmed) return;

    setDeletingId(item.id);
    try {
      await selectionProcessesAdmin.destroy(item.id);
      toast.success('Seletivo excluído.');
      await loadItems();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      const message = Array.isArray(apiErrors) ? apiErrors.map(e => e.detail || e.message || JSON.stringify(e)).join('; ') : err?.message || 'Erro ao excluir seletivo.';
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {/* Status pill tabs */}
      <div className="mb-4">
        <Tabs
          value={statusFilter}
          onValueChange={setStatusFilter}
        >
          <TabsList>
            {STATUS_FILTERS.map(f => (
              <TabsTrigger
                key={f.value}
                value={f.value}
              >
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Filter row + action button */}
      <Filter variant="plain">
        <FilterContent>
          <FilterRow className="min-w-[280px]">
            <Label htmlFor="search-name">Buscar</Label>
            <Input
              id="search-name"
              type="search"
              placeholder="Buscar por nome do seletivo..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </FilterRow>
        </FilterContent>

        <FilterActions>
          <Button onClick={() => navigate(PATHS.ADMIN_SELETIVO.NEW)}>Novo seletivo</Button>
        </FilterActions>
      </Filter>

      {/* Content */}
      {loading ? (
        <Loader />
      ) : (
        <Table className="min-w-[800px]">
          <TableHeaderWrapper tableHead={TABLE_HEAD} />
          <TableBody>
            {filteredItems.map(p => {
              const a = p.attributes;
              const cfg = STATUS_CONFIG[a.current_status ?? a.status] ?? FALLBACK_STATUS;
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    <Link
                      to={PATHS.ADMIN_SELETIVO.DETAIL(p.id)}
                      className="font-semibold text-primary-dark hover:underline"
                    >
                      {a.name}
                    </Link>
                    <div className="text-xs text-neutral">/{a.slug}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={cfg.variant}>{cfg.label}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="neutral-lightest">{AUDIENCE_LABEL[a.target_audience] || 'Aluno'}</Badge>
                  </TableCell>
                  <TableCell className="tabular-nums">{a.registrations_count}</TableCell>
                  <TableCell className="tabular-nums">{formatDate(a.ends_at)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => navigate(PATHS.ADMIN_SELETIVO.DETAIL(p.id))}
                      >
                        <ButtonIcon>
                          <FiEye />
                        </ButtonIcon>{' '}
                        Visualizar
                      </Button>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => navigate(PATHS.ADMIN_SELETIVO.EDIT(p.id))}
                      >
                        <ButtonIcon>
                          <FiEdit3 />
                        </ButtonIcon>{' '}
                        Editar
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="xs"
                        onClick={() => handleDelete(p)}
                        disabled={deletingId === p.id}
                      >
                        <ButtonIcon>
                          <FiTrash2 />
                        </ButtonIcon>{' '}
                        {deletingId === p.id ? 'Excluindo...' : 'Excluir'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableNoData
              notFound={filteredItems.length === 0}
              colSpan={TABLE_HEAD.length}
            />
          </TableBody>
        </Table>
      )}
    </DashboardContent>
  );
}
